import { STOCK_RESOURCES, isBuildingTypeId, type BuildingTypeId, type ResourceAmounts, type StockResource } from '../buildings/definitions';
import type { PlayerId } from '../ids';
import type { Simulation } from '../Simulation';
import { PLAYER_UNIT_TYPES, type UnitTypeId } from '../units/unitDefs';

/**
 * A saved base for Raid & Domination (Phase 13, DECISIONS D93–D97): building
 * layout relative to the Command Center's origin, levels, a small garrison and
 * the stockpile raiders can loot. Plain JSON, validated before use.
 */
export interface BaseLayout {
  readonly v: 1;
  readonly name: string;
  readonly buildings: readonly { readonly type: BuildingTypeId; readonly dx: number; readonly dy: number; readonly level: number }[];
  readonly garrison: readonly { readonly type: UnitTypeId; readonly dx: number; readonly dy: number }[];
  readonly stored: ResourceAmounts;
}

export const BASE_LIMITS = { buildings: 80, garrison: 12, reach: 16, stored: 5000 } as const;

/** Take a snapshot of a player's colony as a raid base (complete buildings only; units near the base as garrison). */
export function captureBase(sim: Simulation, player: PlayerId, name: string): BaseLayout | null {
  const cc = sim.buildings.ownedBy(player).find((b) => b.type === 'command_center');
  if (!cc) return null;
  const buildings = sim.buildings
    .ownedBy(player)
    .filter((b) => b.state === 'complete' && b.type !== 'command_center')
    .map((b) => ({ type: b.type, dx: b.origin.x - cc.origin.x, dy: b.origin.y - cc.origin.y, level: b.level }))
    .filter((b) => Math.max(Math.abs(b.dx), Math.abs(b.dy)) <= BASE_LIMITS.reach)
    .slice(0, BASE_LIMITS.buildings);
  const garrison = sim.units
    .ownedBy(player)
    .map((u) => ({ type: u.type, dx: Math.round(u.x) - cc.origin.x, dy: Math.round(u.y) - cc.origin.y }))
    .filter((u) => Math.max(Math.abs(u.dx), Math.abs(u.dy)) <= BASE_LIMITS.reach)
    .slice(0, BASE_LIMITS.garrison);
  const ledger = sim.economy.ledger(player);
  const stored: Partial<Record<StockResource, number>> = {};
  for (const r of STOCK_RESOURCES) stored[r] = ledger.get(r);
  return { v: 1, name: name.slice(0, 30) || 'My colony', buildings, garrison, stored };
}

/** Why a layout cannot be used (null = valid). Never trust a layout from storage or the network. */
export function validateBase(raw: unknown): string | null {
  const b = raw as BaseLayout;
  if (!b || typeof b !== 'object' || b.v !== 1) return 'Not a base layout.';
  if (typeof b.name !== 'string' || b.name.length > 30) return 'Bad name.';
  if (!Array.isArray(b.buildings) || b.buildings.length > BASE_LIMITS.buildings) return 'Too many buildings.';
  for (const x of b.buildings) {
    if (!x || !isBuildingTypeId(x.type) || x.type === 'command_center') return 'Unknown building.';
    if (![x.dx, x.dy].every((n) => Number.isInteger(n) && Math.abs(n) <= BASE_LIMITS.reach)) return 'Building out of reach.';
    if (x.level !== 1 && x.level !== 2) return 'Bad level.';
  }
  if (!Array.isArray(b.garrison) || b.garrison.length > BASE_LIMITS.garrison) return 'Garrison too large.';
  for (const u of b.garrison) {
    if (!u || !PLAYER_UNIT_TYPES.includes(u.type)) return 'Unknown unit.';
    if (![u.dx, u.dy].every((n) => Number.isInteger(n) && Math.abs(n) <= BASE_LIMITS.reach)) return 'Unit out of reach.';
  }
  if (!b.stored || typeof b.stored !== 'object') return 'Bad stockpile.';
  for (const r of STOCK_RESOURCES) {
    const v = b.stored[r] ?? 0;
    if (!Number.isInteger(v) || v < 0 || v > BASE_LIMITS.stored) return 'Bad stockpile.';
  }
  return null;
}

/** Hand-made bases to raid offline (and to fill the opponent list before other players have saved theirs). */
export const SAMPLE_BASES: readonly BaseLayout[] = [
  {
    v: 1,
    name: 'Scrapper Outpost',
    buildings: [
      { type: 'warehouse', dx: 4, dy: 0, level: 1 },
      { type: 'survivor_housing', dx: -3, dy: 0, level: 1 },
      { type: 'watchtower', dx: 0, dy: -3, level: 1 },
      { type: 'farm', dx: 0, dy: 4, level: 1 },
    ],
    garrison: [
      { type: 'ranger', dx: 1, dy: -4 },
      { type: 'ranger', dx: 2, dy: -4 },
    ],
    stored: { wood: 180, scrap: 120, food: 90 },
  },
  {
    v: 1,
    name: 'Walled Farmstead',
    buildings: [
      ...[-2, -1, 0, 1, 2, 3, 4].map((d) => ({ type: 'wall' as const, dx: d, dy: -3, level: 1 })),
      ...[-2, -1, 0, 1, 2, 3, 4].map((d) => ({ type: 'wall' as const, dx: -3, dy: d, level: 2 })),
      { type: 'gate', dx: 5, dy: -3, level: 1 },
      { type: 'watchtower', dx: 4, dy: 4, level: 2 },
      { type: 'warehouse', dx: 4, dy: 0, level: 2 },
      { type: 'farm', dx: 0, dy: 4, level: 1 },
    ],
    garrison: [
      { type: 'heavy', dx: 1, dy: -2 },
      { type: 'ranger', dx: 3, dy: -2 },
      { type: 'engineer', dx: 3, dy: 3 },
    ],
    stored: { wood: 260, scrap: 220, food: 140 },
  },
  {
    v: 1,
    name: 'Iron Bastion',
    buildings: [
      { type: 'generator', dx: -3, dy: 3, level: 2 },
      { type: 'gun_tower', dx: 3, dy: -3, level: 1 },
      { type: 'gun_tower', dx: -3, dy: -3, level: 1 },
      { type: 'watchtower', dx: 4, dy: 3, level: 2 },
      { type: 'warehouse', dx: 4, dy: 0, level: 2 },
      { type: 'barracks', dx: -4, dy: 0, level: 1 },
    ],
    garrison: [
      { type: 'heavy', dx: 1, dy: -2 },
      { type: 'heavy', dx: 2, dy: -2 },
      { type: 'ranger', dx: 0, dy: 3 },
      { type: 'ranger', dx: 1, dy: 3 },
    ],
    stored: { wood: 400, scrap: 360, food: 200 },
  },
];

/** The attacker's army in a raid (placeholder balance). */
export const RAID_ARMY: Readonly<Partial<Record<UnitTypeId, number>>> = { ranger: 8, heavy: 4, engineer: 1 };

export interface RaidScore {
  /** Share of the defender's buildings (incl. the Command Center) destroyed, 0..1. */
  readonly destroyed: number;
  readonly commandCenter: boolean;
  /** 0–3: ≥ 50 % destroyed, Command Center destroyed, everything destroyed. */
  readonly stars: number;
  /** Resources taken: a capped share of the defender's stockpile (anti-farming: never more than 30 %). */
  readonly loot: ResourceAmounts;
  /** Ranking points: attacker gains, defender loses (capped). */
  readonly attackerPoints: number;
  readonly defenderPoints: number;
}

export const RAID_LOOT_CAP = 0.3;

export function scoreRaid(totalBuildings: number, destroyedBuildings: number, commandCenter: boolean, stored: ResourceAmounts): RaidScore {
  const destroyed = totalBuildings > 0 ? Math.min(1, destroyedBuildings / totalBuildings) : 0;
  const stars = (destroyed >= 0.5 ? 1 : 0) + (commandCenter ? 1 : 0) + (destroyed >= 1 ? 1 : 0);
  const share = Math.min(RAID_LOOT_CAP, 0.2 * destroyed + (commandCenter ? 0.1 : 0));
  const loot: Partial<Record<StockResource, number>> = {};
  for (const r of STOCK_RESOURCES) loot[r] = Math.floor((stored[r] ?? 0) * share);
  return { destroyed, commandCenter, stars, loot, attackerPoints: stars ? stars * 10 : -5, defenderPoints: stars ? -stars * 5 : 5 };
}
